import React, { useEffect, useMemo, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import {
  WxbAlert,
  WxbButton,
  WxbDataTable,
  WxbSearchInput,
  WxbSelect,
  WxbStatusBadge,
  WxbTag,
} from '../wxb-ui';
import { RESOURCE_KIND_LABELS } from './equipmentStateCatalogModel';
import type {
  EquipmentStateBinding,
  EquipmentStateClass,
  EquipmentPolicyOverride,
  EquipmentStateTopologyResource,
} from '../../types/prodEquipmentStateCatalog';

interface Props {
  resources: EquipmentStateTopologyResource[];
  classes: EquipmentStateClass[];
  bindings: EquipmentStateBinding[];
  readOnly: boolean;
  saving: boolean;
  onSave: (bindings: EquipmentStateBinding[]) => Promise<boolean>;
  onDirtyChange?: (dirty: boolean) => void;
}

const ResourceBindingPanel: React.FC<Props> = ({
  resources,
  classes,
  bindings,
  readOnly,
  saving,
  onSave,
  onDirtyChange,
}) => {
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [draftOverrides, setDraftOverrides] = useState<Record<string, EquipmentPolicyOverride[]>>({});
  const [search, setSearch] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDraft(Object.fromEntries(bindings.map((binding) => [binding.resourceId, binding.classId])));
    setDraftOverrides(Object.fromEntries(bindings.map((binding) => [
      binding.resourceId,
      JSON.parse(JSON.stringify(binding.policyOverrides ?? [])) as EquipmentPolicyOverride[],
    ])));
    setDirty(false);
    onDirtyChange?.(false);
  }, [bindings, onDirtyChange]);

  const activeResources = resources.filter((resource) => resource.lifecycleStatus === 'ACTIVE');
  const activeBoundCount = activeResources.filter((resource) => Boolean(draft[resource.resourceId])).length;
  const invalidBindings = resources.filter((resource) => {
    const classId = draft[resource.resourceId];
    if (!classId) return false;
    if (resource.lifecycleStatus === 'RETIRED') return true;
    const equipmentClass = classes.find((item) => item.classId === classId);
    return !equipmentClass
      || equipmentClass.lifecycleStatus !== 'ACTIVE'
      || !equipmentClass.applicableResourceKinds.includes(resource.kind);
  });
  const resourceIds = new Set(resources.map((resource) => resource.resourceId));
  const orphanBindings = Object.entries(draft)
    .filter(([resourceId]) => !resourceIds.has(resourceId))
    .map(([resourceId, classId]) => ({
      resourceId,
      classId,
      className: classes.find((item) => item.classId === classId)?.name ?? classId,
    }));

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return resources;
    return resources.filter((resource) => (
      resource.code.toLowerCase().includes(query)
      || resource.name.toLowerCase().includes(query)
      || RESOURCE_KIND_LABELS[resource.kind].toLowerCase().includes(query)
    ));
  }, [resources, search]);

  const columns: ColumnsType<EquipmentStateTopologyResource> = [
    {
      title: '资源',
      key: 'resource',
      render: (_, resource) => (
        <div>
          <div className="esc-table-primary">{resource.name}</div>
          <code className="esc-table-code">{resource.code}</code>
        </div>
      ),
    },
    {
      title: '类型',
      dataIndex: 'kind',
      width: 120,
      render: (kind: EquipmentStateTopologyResource['kind']) => (
        <WxbTag color="neutral">{RESOURCE_KIND_LABELS[kind]}</WxbTag>
      ),
    },
    {
      title: '生命周期',
      dataIndex: 'lifecycleStatus',
      width: 120,
      render: (status: EquipmentStateTopologyResource['lifecycleStatus']) => (
        <WxbStatusBadge
          status={status === 'ACTIVE' ? 'success' : status === 'INACTIVE' ? 'warning' : 'disabled'}
          label={status === 'ACTIVE' ? '启用' : status === 'INACTIVE' ? '停用' : '已退役'}
        />
      ),
    },
    {
      title: '设备类别',
      key: 'class',
      render: (_, resource) => {
        const currentClassId = draft[resource.resourceId];
        const compatible = classes.filter((equipmentClass) => (
          equipmentClass.lifecycleStatus === 'ACTIVE'
          && equipmentClass.applicableResourceKinds.includes(resource.kind)
        ));
        const currentClass = classes.find((equipmentClass) => equipmentClass.classId === currentClassId);
        const options = compatible.map((equipmentClass) => ({
          value: equipmentClass.classId,
          label: `${equipmentClass.name} (${equipmentClass.code})`,
        }));
        if (currentClass && !options.some((option) => option.value === currentClass.classId)) {
          options.push({ value: currentClass.classId, label: `${currentClass.name} (${currentClass.code}) · 不兼容` });
        }
        if (resource.lifecycleStatus === 'RETIRED') {
          return currentClassId ? (
            <div className="esc-retired-binding">
              <span>{currentClass?.name ?? currentClassId}</span>
              {!readOnly && (
                <WxbButton
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setDraft((current) => {
                      const next = { ...current };
                      delete next[resource.resourceId];
                      return next;
                    });
                    setDraftOverrides((current) => {
                      const next = { ...current };
                      delete next[resource.resourceId];
                      return next;
                    });
                    setDirty(true);
                    onDirtyChange?.(true);
                  }}
                >
                  移除绑定
                </WxbButton>
              )}
            </div>
          ) : <span className="esc-condition-no-value">已退役，不可新绑定</span>;
        }
        return (
          <WxbSelect
            aria-label={`${resource.name} 设备类别`}
            allowClear
            value={currentClassId || undefined}
            disabled={readOnly}
            options={options}
            placeholder={compatible.length > 0 ? '选择设备类别' : '没有兼容类别'}
            onChange={(classId) => {
              setDraft((current) => {
                const next = { ...current };
                if (classId) next[resource.resourceId] = String(classId);
                else delete next[resource.resourceId];
                return next;
              });
              setDraftOverrides((current) => {
                const next = { ...current };
                next[resource.resourceId] = [];
                if (!classId) delete next[resource.resourceId];
                return next;
              });
              setDirty(true);
              onDirtyChange?.(true);
            }}
          />
        );
      },
    },
  ];

  const save = async () => {
    const nextBindings = Object.entries(draft)
      .filter(([, classId]) => Boolean(classId))
      .map(([resourceId, classId]) => ({
        resourceId,
        classId,
        policyOverrides: draftOverrides[resourceId] ?? [],
      }));
    const saved = await onSave(nextBindings);
    if (saved) {
      setDirty(false);
      onDirtyChange?.(false);
    }
  };

  return (
    <div className="esc-binding-panel">
      <div className="esc-binding-summary">
        <div>
          <strong>{activeBoundCount} / {activeResources.length}</strong>
          <span>启用资源已绑定</span>
        </div>
        <p>发布要求拓扑基线内所有启用 ROOM、EQUIPMENT、COMPONENT、CIP_STATION 资源完成类别绑定。</p>
      </div>

      {invalidBindings.length > 0 && (
        <WxbAlert variant="error" title={`${invalidBindings.length} 条绑定与当前拓扑或类别不兼容`}>
          请重新选择类别；同步拓扑基线后保留但无法解析的绑定也会在服务端校验问题中列出。
        </WxbAlert>
      )}
      {orphanBindings.length > 0 && (
        <WxbAlert variant="error" title={`${orphanBindings.length} 条绑定在当前拓扑基线中无法解析`}>
          <div className="esc-orphan-bindings">
            <ul>
              {orphanBindings.map((binding) => (
                <li key={binding.resourceId}>
                  <code>{binding.resourceId}</code>
                  <span>原类别：{binding.className}</span>
                </li>
              ))}
            </ul>
            {!readOnly && (
              <WxbButton
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setDraft((current) => Object.fromEntries(
                    Object.entries(current).filter(([resourceId]) => resourceIds.has(resourceId)),
                  ));
                  setDraftOverrides((current) => Object.fromEntries(
                    Object.entries(current).filter(([resourceId]) => resourceIds.has(resourceId)),
                  ));
                  setDirty(true);
                  onDirtyChange?.(true);
                }}
              >
                移除失效绑定
              </WxbButton>
            )}
          </div>
        </WxbAlert>
      )}

      <div className="esc-panel-toolbar">
        <WxbSearchInput
          value={search}
          placeholder="搜索资源编码、名称或类型"
          aria-label="搜索待绑定资源"
          onChange={setSearch}
        />
        {!readOnly && (
          <WxbButton
            type="button"
            disabled={!dirty || saving || invalidBindings.length > 0 || orphanBindings.length > 0}
            onClick={save}
          >
            {saving ? '保存中...' : '保存全部绑定'}
          </WxbButton>
        )}
      </div>

      <WxbDataTable<EquipmentStateTopologyResource>
        rowKey="resourceId"
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 12, showSizeChanger: false }}
        density="compact"
        emptyState={{
          description: search ? '没有符合搜索条件的资源。' : '当前拓扑基线没有可绑定资源。',
        }}
      />

    </div>
  );
};

export default ResourceBindingPanel;
